import { isSubagentSessionKey } from "../../../../src/routing/session-key.js";
import type { ChatSubagentCard } from "../ui-types.ts";
import { promptCollapsedPreview } from "./collapsible-markdown-prompt.ts";
import { isSubagentRunActive } from "./subagent-run-status.ts";

/** True for spawned subagent runs (pending spawn has no child key yet). */
export function isSubagentRunCard(card: ChatSubagentCard): boolean {
  const child = card.childSessionKey?.trim() ?? "";
  if (!child) {
    return isSubagentRunActive(card.status);
  }
  return isSubagentSessionKey(child) || child.includes(":subagent:");
}

export function filterSubagentRunCards(cards: ChatSubagentCard[]): ChatSubagentCard[] {
  return cards.filter(isSubagentRunCard);
}

function truncate(text: string, max: number): string {
  const t = text.trim();
  if (t.length <= max) {
    return t;
  }
  return `${t.slice(0, max - 1)}…`;
}

/** Primary title for a subagent run row (task-first, not configured agent name). */
export function subagentRunTitle(card: ChatSubagentCard): string {
  const task = card.task?.trim() ?? "";
  const label = card.label?.trim() ?? "";
  if (task.length > 0 && task !== label) {
    return promptCollapsedPreview(task, 72) || truncate(task, 72);
  }
  if (label.length > 0 && !/^subagent\b/i.test(label)) {
    return `Subagent · ${truncate(label, 48)}`;
  }
  if (label.length > 0) {
    return truncate(label, 72);
  }
  return `Subagent · ${card.agentId}`;
}

export function subagentRunAgentLine(card: ChatSubagentCard): string {
  const parts = [`@${card.agentId}`];
  if (card.model?.trim()) {
    parts.push(card.model.trim());
  }
  const child = card.childSessionKey?.trim();
  if (child) {
    const short = child.split(":").slice(-2).join(":");
    parts.push(short);
  }
  return parts.join(" · ");
}

export function subagentSpawnLabel(agentDisplayName: string, agentId: string): string {
  const name = agentDisplayName.trim() || agentId;
  return name === agentId ? `Subagent @${agentId}` : `Subagent · ${name}`;
}

/**
 * Parent → children hierarchy: resolve, for a given child card, the
 * parent card in the supplied in-scope set (if any), plus a short
 * human-readable label and the short session id for the badge. Also
 * flags whether this child is a descendant of the currently-selected
 * parent (used by the in-thread chat row and the Subagents inspector
 * to apply the `--child-highlighted` treatment).
 *
 * The lookup is O(N) (single pass to build a `childSessionKey → card`
 * map). The selection match is generous: it accepts the parent's
 * `childSessionKey`, `runId`, or `id` as the "selected id" — the
 * same three keys the renderer uses when the parent card exposes its
 * stable id. This is what lets the in-thread row agree with the
 * inspector without coordinating on a single canonical key.
 */
export function resolveSubagentParentContext(
  card: ChatSubagentCard,
  allInScope: ChatSubagentCard[],
  selectedParentId: string | null | undefined,
): {
  parent: ChatSubagentCard | null;
  parentLabel: string | null;
  parentShortId: string | null;
  isHighlightedChild: boolean;
} {
  if (!card.parentSessionKey?.trim()) {
    return { parent: null, parentLabel: null, parentShortId: null, isHighlightedChild: false };
  }
  const byKey = new Map<string, ChatSubagentCard>();
  for (const c of allInScope) {
    const k = c.childSessionKey?.trim();
    if (k) {
      byKey.set(k, c);
    }
  }
  const parent = byKey.get(card.parentSessionKey.trim()) ?? null;
  if (!parent) {
    return { parent: null, parentLabel: null, parentShortId: null, isHighlightedChild: false };
  }
  const parentLabel =
    (parent.task?.trim() || parent.label?.trim() || "").slice(0, 64) || null;
  const parentShortId = parent.childSessionKey
    ? parent.childSessionKey.split(":").slice(-2).join(":")
    : null;
  let isHighlightedChild = false;
  if (selectedParentId) {
    isHighlightedChild =
      parent.childSessionKey === selectedParentId ||
      parent.runId === selectedParentId ||
      parent.id === selectedParentId;
  }
  return { parent, parentLabel, parentShortId, isHighlightedChild };
}
